import { signal } from '@angular/core';
import { ApiResponse } from './apiresponse.model';
import { appConfig } from '../app-config';
import { BaseApi } from '@core/api/base.api';

export abstract class RestApi<T> extends BaseApi {
  abstract endpoint: string;

  isLoading = signal(false);

  async getList(params?: {}, abortSignal?: AbortSignal) {
    let url = `${appConfig.apiRoot}${this.endpoint}`;
    if (params) {
      url = `${url}?${new URLSearchParams(params)}`;
    }
    const options = await this.getOptions(abortSignal);
    return await this.fetchList(url, options);
  }

  async getById(id: number, abortSignal?: AbortSignal) {
    const url = `${appConfig.apiRoot}${this.endpoint}/${id}`;
    const options = await this.getOptions(abortSignal);
    return await this.fetchSingle(url, options);
  }

  async create(value: Partial<T>) {
    const url = `${appConfig.apiRoot}${this.endpoint}`;
    const options = await this.getOptions();
    options.method = 'POST';
    options.body = JSON.stringify(value);
    return await this.fetchSingle(url, options);
  }

  async createMultipart(value: FormData) {
    const url = `${appConfig.apiRoot}${this.endpoint}`;
    const options = await this.getOptions();
    (options.headers as Headers).delete('content-type'); //Allows the browser to set the correct multipart boundary
    options.method = 'POST';
    options.body = value;
    return await this.fetchSingle(url, options);
  }

  async update(id: number, value: Partial<T>) {
    const url = `${appConfig.apiRoot}${this.endpoint}/${id}`;
    const options = await this.getOptions();
    options.method = 'PUT';
    options.body = JSON.stringify(value);
    return this.fetchSingle(url, options);
  }

  async updateMultipart(id: number, value: FormData) {
    const url = `${appConfig.apiRoot}${this.endpoint}/${id}`;
    const options = await this.getOptions();
    (options.headers as Headers).delete('content-type'); //Allows the browser to set the correct multipart boundary
    options.method = 'PUT';
    options.body = value;
    return await this.fetchSingle(url, options);
  }

  async remove(id: number) {
    const url = `${appConfig.apiRoot}${this.endpoint}/${id}`;
    const options = await this.getOptions();
    options.method = 'DELETE';
    return await this.fetchSingle(url, options);
  }

  async fetchSingle<U = T>(url: string, options: RequestInit) {
    this.isLoading.set(true);

    let response: Response;
    try {
      response = await fetch(url, options);
    } catch (error) {
      console.error(error);
      throw error;
    }

    if (!response.ok) {
      throw new Error('Unable to load from API: Status=' + response.status);
    }
    const apiResponse = (await response.json()) as ApiResponse<U>;
    if (!apiResponse.success) {
      throw new Error('Error from API: ' + apiResponse.message);
    }
    this.isLoading.set(false);
    return apiResponse.data;
  }

  async fetchList<U = T>(url: string, options: RequestInit) {
    let response: Response;
    try {
      response = await fetch(url, options);
    } catch (error) {
      console.error(error);
      throw error;
    }
    if (!response.ok) {
      throw new Error('Unable to load from API: Status=' + response.status);
    }
    const apiResponse = (await response.json()) as ApiResponse<U[]>;
    if (!apiResponse.success) {
      throw new Error('Error from API: ' + apiResponse.message);
    }
    return apiResponse;
  }
}
